// ANANT CORPUS50 — 72-month SIP crowdfund for Samadhan Center
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Loader2, TrendingUp, Award, Users, Zap, Send, Clock, Shield } from 'lucide-react';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';

const PROJECT_END = new Date('2028-12-31T23:59:59Z');

// Certificate themes — palette + fonts per theme
const CERT_THEMES = {
  royal_blue_gold: {
    label: 'Royal Blue & Gold', emoji: '🏛️',
    parchment: 'linear-gradient(135deg,#faf3d9 0%,#f5efd6 40%,#efe3b8 100%)',
    border1: '#1e3a8a', border2: '#c8a24a',
    ink: '#1a1a3a', accent: '#1e3a8a', gold: '#c8a24a', goldLight: '#e6c973',
    ribbon: 'linear-gradient(90deg,#c8a24a 0%,#e6c973 50%,#c8a24a 100%)',
    guillocheColor1: 'rgba(30,58,138,0.06)', guillocheColor2: 'rgba(200,162,74,0.06)',
    sealBg: 'conic-gradient(from 0deg,#c8a24a,#e6c973,#f0e5c0,#c8a24a,#1e3a8a,#3b56a8,#1e3a8a,#c8a24a)',
  },
  emerald_regal: {
    label: 'Emerald Regal', emoji: '🌿',
    parchment: 'linear-gradient(135deg,#fdf6e3 0%,#f5f5dc 40%,#e8dfc2 100%)',
    border1: '#065f46', border2: '#d4a017',
    ink: '#0f172a', accent: '#065f46', gold: '#d4a017', goldLight: '#f0d060',
    ribbon: 'linear-gradient(90deg,#065f46 0%,#0e7a5c 50%,#065f46 100%)',
    guillocheColor1: 'rgba(6,95,70,0.07)', guillocheColor2: 'rgba(212,160,23,0.06)',
    sealBg: 'conic-gradient(from 0deg,#d4a017,#f0d060,#fff2a0,#d4a017,#065f46,#0e7a5c,#065f46,#d4a017)',
  },
  crimson_gold: {
    label: 'Crimson Gold', emoji: '❤️',
    parchment: 'linear-gradient(135deg,#fff7ed 0%,#fef3c7 40%,#fde68a 100%)',
    border1: '#7f1d1d', border2: '#eab308',
    ink: '#3b0a0a', accent: '#7f1d1d', gold: '#eab308', goldLight: '#fcd34d',
    ribbon: 'linear-gradient(90deg,#7f1d1d 0%,#b91c1c 50%,#7f1d1d 100%)',
    guillocheColor1: 'rgba(127,29,29,0.07)', guillocheColor2: 'rgba(234,179,8,0.06)',
    sealBg: 'conic-gradient(from 0deg,#eab308,#fcd34d,#fff2a0,#eab308,#7f1d1d,#b91c1c,#7f1d1d,#eab308)',
  },
  midnight_platinum: {
    label: 'Midnight Platinum', emoji: '🌌',
    parchment: 'linear-gradient(135deg,#f8fafc 0%,#e2e8f0 40%,#cbd5e1 100%)',
    border1: '#0f172a', border2: '#94a3b8',
    ink: '#0f172a', accent: '#0f172a', gold: '#94a3b8', goldLight: '#cbd5e1',
    ribbon: 'linear-gradient(90deg,#0f172a 0%,#334155 50%,#0f172a 100%)',
    guillocheColor1: 'rgba(15,23,42,0.08)', guillocheColor2: 'rgba(148,163,184,0.10)',
    sealBg: 'conic-gradient(from 0deg,#94a3b8,#cbd5e1,#e2e8f0,#94a3b8,#0f172a,#334155,#0f172a,#94a3b8)',
  },
  saffron_maroon: {
    label: 'Saffron & Maroon', emoji: '🕉️',
    parchment: 'linear-gradient(135deg,#fef3c7 0%,#fde68a 40%,#fcd34d 100%)',
    border1: '#7c2d12', border2: '#f59e0b',
    ink: '#3b0a0a', accent: '#7c2d12', gold: '#f59e0b', goldLight: '#fbbf24',
    ribbon: 'linear-gradient(90deg,#7c2d12 0%,#dc2626 50%,#7c2d12 100%)',
    guillocheColor1: 'rgba(124,45,18,0.07)', guillocheColor2: 'rgba(245,158,11,0.07)',
    sealBg: 'conic-gradient(from 0deg,#f59e0b,#fbbf24,#fef3c7,#f59e0b,#7c2d12,#dc2626,#7c2d12,#f59e0b)',
  },
};

// Reusable guilloché SVG pattern (subtle spirograph-style) — data-URI, no external asset
const guillocheSVG = (c1, c2) => `url("data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'>
    <defs>
      <pattern id='g' x='0' y='0' width='120' height='120' patternUnits='userSpaceOnUse'>
        <circle cx='60' cy='60' r='55' fill='none' stroke='${c1}' stroke-width='0.6'/>
        <circle cx='60' cy='60' r='45' fill='none' stroke='${c2}' stroke-width='0.5'/>
        <circle cx='60' cy='60' r='35' fill='none' stroke='${c1}' stroke-width='0.5'/>
        <circle cx='60' cy='60' r='25' fill='none' stroke='${c2}' stroke-width='0.4'/>
        <path d='M60,5 L60,115 M5,60 L115,60 M20,20 L100,100 M100,20 L20,100' stroke='${c1}' stroke-width='0.3'/>
        <path d='M40,10 Q60,60 40,110 M80,10 Q60,60 80,110 M10,40 Q60,60 10,80 M110,40 Q60,60 110,80' stroke='${c2}' stroke-width='0.4' fill='none'/>
      </pattern>
    </defs>
    <rect width='240' height='240' fill='url(%23g)'/>
  </svg>`
)}")`;


const useCountdown = (target) => {
  const [t, setT] = useState(() => Math.max(0, target.getTime() - Date.now()));
  useEffect(() => {
    const i = setInterval(() => setT(Math.max(0, target.getTime() - Date.now())), 1000);
    return () => clearInterval(i);
  }, [target]);
  const days = Math.floor(t / 86400000);
  const hours = Math.floor((t % 86400000) / 3600000);
  const mins = Math.floor((t % 3600000) / 60000);
  const secs = Math.floor((t % 60000) / 1000);
  return { days, hours, mins, secs };
};

export default function Corpus50() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [tab, setTab] = useState('overview');

  // Start-plan form
  const [selPlan, setSelPlan] = useState(null);
  const [nomName, setNomName] = useState('');
  const [nomRel, setNomRel] = useState('');
  const [nomMobile, setNomMobile] = useState('');
  const [nomAadhaar, setNomAadhaar] = useState('');
  const [nomPan, setNomPan] = useState('');
  const [kycAadhaarNo, setKycAadhaarNo] = useState('');
  const [kycPanNo, setKycPanNo] = useState('');
  const [kycAadhaarImg, setKycAadhaarImg] = useState(null);
  const [kycPanImg, setKycPanImg] = useState(null);
  // Consent flow
  const [showConsent, setShowConsent] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [bondModal, setBondModal] = useState(null);       // { sub, bond, consent }
  const [bondLoading, setBondLoading] = useState(false);
  const [certTheme, setCertTheme] = useState(() => localStorage.getItem('corpus50_cert_theme') || 'royal_blue_gold');
  useEffect(() => { localStorage.setItem('corpus50_cert_theme', certTheme); }, [certTheme]);
  // Sync admin default when config loads (if user hasn't picked one yet — first visit)
  useEffect(() => {
    const stored = localStorage.getItem('corpus50_cert_theme_user_picked');
    if (!stored && config?.certificate?.default_theme) setCertTheme(config.certificate.default_theme);
  }, [config]);

  const active = config?.user?.active_subscription;
  const cd = useCountdown(PROJECT_END);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, p, lb] = await Promise.all([
        api.get('/corpus50/config'),
        api.get('/corpus50/stats'),
        api.get('/corpus50/my-payments').catch(() => ({ data: { payments: [] } })),
        api.get('/corpus50/leaderboard').catch(() => ({ data: null })),
      ]);
      setConfig(c.data);
      setStats(s.data);
      setPayments(p.data?.payments || []);
      setLeaderboard(lb.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
    } finally { setLoading(false); }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const start = () => {
    if (!selPlan) { toast.error('Select a plan first'); return; }
    if (!nomName.trim() || !nomRel.trim() || !nomMobile.trim()) { toast.error('Fill nominee details'); return; }
    // Show mandatory consent modal — user must read + tick "I Agree" before first SIP
    setAgreed(false);
    setShowConsent(true);
  };

  const confirmAndStart = async () => {
    if (!agreed) { toast.error('Please tick "I Agree" to proceed'); return; }
    setStarting(true);
    try {
      try {
        const kyc = { aadhaar_no: kycAadhaarNo, pan_no: kycPanNo, aadhaar_img: kycAadhaarImg, pan_img: kycPanImg, saved_at: new Date().toISOString() };
        localStorage.setItem(`corpus50:kyc:${selPlan.id}`, JSON.stringify(kyc));
      } catch (_) { /* storage full — non-blocking */ }
      const r = await api.post('/corpus50/start-plan', {
        plan_id: selPlan.id,
        nominee_name: nomName.trim(),
        nominee_relation: nomRel.trim(),
        nominee_mobile: nomMobile.trim(),
        nominee_aadhaar: nomAadhaar.trim() || null,
        nominee_pan: nomPan.trim() || null,
        consent_agreed: true,
        consent_text_hash: 'corpus50-consent-v1',
      });
      const bondNo = r?.data?.bond_no;
      toast.success(`🎉 Corpus50 S.I.P. started! Bond No: ${bondNo}`);
      setShowConsent(false);
      await load();
      setTab('my-plan');
      // Auto-open bond viewer so user sees their agreement immediately
      if (r?.data?.subscription?.id) openBond(r.data.subscription.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Start failed'); }
    finally { setStarting(false); }
  };

  const openBond = async (subId) => {
    setBondLoading(true);
    try {
      const [bR, cR] = await Promise.all([
        api.get(`/corpus50/bond/${subId}`),
        api.get(`/corpus50/consent/${subId}`).catch(() => ({ data: null })),
      ]);
      setBondModal({ bond: bR.data, consent: cR.data?.consent });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bond load failed'); }
    finally { setBondLoading(false); }
  };

  const onKycImg = (which, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Max 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { if (which === 'aadhaar') setKycAadhaarImg(ev.target.result); else setKycPanImg(ev.target.result); };
    reader.readAsDataURL(f);
  };

  const payEmi = async () => {
    if (!window.confirm(`Next S.I.P. pay karna hai? ${active.monthly_at} AT (+ penalty if late) deduct hoga.`)) return;
    setPaying(true);
    try {
      const r = await api.post('/corpus50/pay-emi', {});
      toast.success(`✅ S.I.P. ${r.data.month_index}/72 paid! Receipt: ${r.data.receipt_no}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Payment failed'); }
    finally { setPaying(false); }
  };

  const foreclose = async () => {
    if (!window.confirm('Foreclose current S.I.P.? Only 70% of paid amount will be refunded (30% deduction). Only valid within first 15 days of starting.')) return;
    try {
      const r = await api.post('/corpus50/foreclose', { subscription_id: active.id });
      toast.success(`Foreclosed. Refund: ${r.data.refund_at} AT`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Foreclose failed'); }
  };

  const shareReferral = () => {
    const url = `${window.location.origin}/corpus50?ref=${config?.user?.active_subscription?.username || ''}`;
    const msg = `🌟 Join me on Anant Corpus50 — 72-month S.I.P. (Systematic Impact Pledge) for Samadhan Center's ₹50 Cr vision!\n\nContribute, earn cashback service + 7% referral rewards.\n\n${url}`;
    if (navigator.share) navigator.share({ title: 'Anant Corpus50', text: msg, url }).catch(() => {});
    else { navigator.clipboard.writeText(msg); toast.success('Link copied to clipboard'); }
  };

  // Days until next SIP due
  const daysUntilDue = useMemo(() => {
    if (!active?.next_due_at) return null;
    try {
      const diff = new Date(active.next_due_at).getTime() - Date.now();
      return Math.max(0, Math.ceil(diff / 86400000));
    } catch { return null; }
  }, [active]);

  const sendReminder = async () => {
    try {
      const r = await api.get('/corpus50/whatsapp-reminder');
      window.open(r.data.wa_link, '_blank');
    } catch { toast.error('Reminder link generate failed'); }
  };

  const progressPct = useMemo(() => {
    if (!stats) return 0;
    return Math.min(100, Math.round((stats.total_collected_at / stats.target_at) * 100));
  }, [stats]);

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-amber-950 via-slate-900 to-black text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-amber-400/50 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white">← Home</Link>
        <ATWalletBadge compact />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Countdown Timer */}
        <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-r from-amber-950/60 via-orange-950/40 to-amber-950/60 p-4 mb-6" data-testid="corpus50-countdown">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 font-black"><Clock className="w-3 h-3" /> PROJECT ENDS · 31 DEC 2028</div>
            <div className="flex justify-center gap-2 mt-2 text-2xl sm:text-4xl font-black">
              <div><span className="text-amber-300">{cd.days}</span><span className="text-[9px] block opacity-70">DAYS</span></div>
              <div className="opacity-50">:</div>
              <div><span className="text-amber-300">{String(cd.hours).padStart(2, '0')}</span><span className="text-[9px] block opacity-70">HRS</span></div>
              <div className="opacity-50">:</div>
              <div><span className="text-amber-300">{String(cd.mins).padStart(2, '0')}</span><span className="text-[9px] block opacity-70">MIN</span></div>
              <div className="opacity-50">:</div>
              <div><span className="text-amber-300">{String(cd.secs).padStart(2, '0')}</span><span className="text-[9px] block opacity-70">SEC</span></div>
            </div>
          </div>
        </div>

        <div className="text-center mb-6">
          <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 bg-clip-text text-transparent">Anant Corpus50</h1>
          <p className="text-sm text-slate-300 mt-2 max-w-2xl mx-auto">72-month <b className="text-amber-200">S.I.P. (Systematic Impact Pledge)</b> crowdfund for <b className="text-amber-300">Samadhan Center</b> — ₹50 Crore AT vision. Nominee-secured. Cashback service redeemable. <span className="text-[10px] opacity-70">*Not a financial investment.</span></p>
        </div>

        {/* Fund progress */}
        {stats && (
          <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 p-4 mb-6" data-testid="corpus50-progress">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black">TOTAL COLLECTED</div>
                <div className="text-3xl font-black text-amber-300">{stats.total_collected_at.toLocaleString()} <span className="text-lg opacity-70">AT</span></div>
                <div className="text-[10px] opacity-70">of 50,00,00,000 AT target</div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-widest opacity-70">SUBSCRIBERS</div>
                <div className="text-2xl font-black">{stats.active_subscribers} <span className="text-sm opacity-70">/ {stats.total_subscribers}</span></div>
              </div>
            </div>
            <div className="mt-3 h-3 w-full bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-yellow-300" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="text-center text-[10px] mt-1 opacity-70">{progressPct}% funded</div>
          </div>
        )}

        {/* Trust & Safety strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="corpus50-trust-strip">
          <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-2 text-center"><div className="text-lg">🔏</div><div className="text-[9px] uppercase opacity-70">Digital Bond</div><div className="text-[10px] font-black text-emerald-300">Every SIP</div></div>
          <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 p-2 text-center"><div className="text-lg">🛡️</div><div className="text-[9px] uppercase opacity-70">Nominee Safe</div><div className="text-[10px] font-black text-cyan-300">KYC secured</div></div>
          <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-2 text-center"><div className="text-lg">📜</div><div className="text-[9px] uppercase opacity-70">Consent Record</div><div className="text-[10px] font-black text-amber-300">Timestamped</div></div>
          <div className="rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-2 text-center"><div className="text-lg">🎁</div><div className="text-[9px] uppercase opacity-70">1.5× Service</div><div className="text-[10px] font-black text-fuchsia-300">Samadhan bonus</div></div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 flex-wrap" data-testid="corpus50-tabs">
          {[['overview', '💰 Plans'], ['my-plan', '📅 My Plan'], ['history', '📜 History'], ['board', '🏆 Leaderboard']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`corpus50-tab-${k}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-black ${tab === k ? 'bg-amber-400 text-black' : 'bg-white/5 text-slate-300'}`}>{l}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            {active && (
              <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/10 p-4 mb-4" data-testid="corpus50-active-info">
                <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-black">✅ ACTIVE S.I.P.</div>
                <div className="text-lg font-black mt-1">{active.plan_label} · {active.months_paid}/72 paid</div>
                <div className="text-xs opacity-80">Next due: {new Date(active.next_due_at).toLocaleDateString()} {daysUntilDue !== null && <b className={daysUntilDue <= 3 ? 'text-rose-300' : 'text-emerald-300'}>({daysUntilDue} days left)</b>}</div>
                <div className="text-[11px] mt-1 text-emerald-200">👉 Aap upar wale plans se bhi additional S.I.P. shuru kar sakte hain (parallel donation).</div>
              </div>
            )}
            {/* Samadhan Center Vision */}
            <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-950/40 to-orange-950/30 p-4 mb-4" data-testid="corpus50-samadhan-vision">
              <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black mb-1">💫 SAMADHAN CENTER VISION</div>
              <div className="text-sm">A physical + digital destination where every Anant member finds <b>peace of mind, celebration of life &amp; community reverence</b> — funded by voluntary community pledges.</div>
              <Link to="/samadhan" className="text-[11px] text-amber-300 hover:underline mt-2 inline-block">→ Explore full Samadhan Center</Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(config?.plans || []).map((p) => (
                <button key={p.id} onClick={() => setSelPlan(p)} data-testid={`corpus50-plan-${p.id}`}
                  className={`text-left rounded-2xl border-2 p-4 bg-gradient-to-br ${p.color} hover:scale-[1.02] ${selPlan?.id === p.id ? 'ring-2 ring-white' : ''}`}>
                  <div className="text-xs uppercase tracking-widest opacity-90 font-black">{p.label}</div>
                  <div className="text-2xl font-black mt-1">{p.monthly_at.toLocaleString()} <span className="text-xs opacity-80">AT/mo</span></div>
                  <div className="text-[11px] opacity-90 mt-2 space-y-0.5">
                    <div>⏳ Duration: <b>72 months</b></div>
                    <div>💰 Total S.I.P. (72 months): <b>{p.total_at.toLocaleString()} AT</b></div>
                    <div>💸 Cash-out / Transfer / Redeem: <b>{p.cashback_at.toLocaleString()} AT</b></div>
                    <div>🏛️ Samadhan Service Redeem Value: <b>{Math.round(p.cashback_at * 1.5).toLocaleString()} AT</b> <span className="opacity-70 text-[10px]">(1.5× bonus)</span></div>
                    <div className="opacity-80 pt-0.5 border-t border-white/10 mt-1">↗️ Reward Multiplier: <b>{(p.cashback_at / p.total_at).toFixed(2)}x</b> · Service: <b>{((p.cashback_at * 1.5) / p.total_at).toFixed(2)}x</b></div>
                  </div>
                </button>
              ))}
            </div>
            {selPlan && (
              <div className="mt-4 rounded-2xl border border-slate-700 bg-slate-900/60 p-4 space-y-3" data-testid="corpus50-start-form">
                <div className="text-sm font-black text-amber-300">Starting: {selPlan.label} · {selPlan.monthly_at} AT/month</div>
                {/* Full plan breakdown */}
                <div className="rounded-lg bg-gradient-to-br from-amber-950/60 to-orange-950/40 border border-amber-400/30 p-3 text-[12px]" data-testid="corpus50-plan-breakdown">
                  <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black mb-1">📊 Full Plan Breakdown</div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>Monthly S.I.P.:</div><div className="text-right"><b>{selPlan.monthly_at.toLocaleString()} AT</b></div>
                    <div>Duration:</div><div className="text-right"><b>72 months</b></div>
                    <div>Total contribution (72m):</div><div className="text-right"><b>{selPlan.total_at.toLocaleString()} AT</b></div>
                    <div className="col-span-2 border-t border-amber-400/20 my-1"></div>
                    <div>💸 Cash-out / Transfer / Redeem at maturity:</div><div className="text-right text-emerald-300"><b>{selPlan.cashback_at.toLocaleString()} AT</b></div>
                    <div>🏛️ Samadhan Center service redeem <span className="opacity-70">(1.5× bonus)</span>:</div><div className="text-right text-cyan-300"><b>{Math.round(selPlan.cashback_at * 1.5).toLocaleString()} AT</b></div>
                    <div className="col-span-2 border-t border-amber-400/20 my-1"></div>
                    <div>Reward Multiplier (cash):</div><div className="text-right text-amber-300"><b>{(selPlan.cashback_at / selPlan.total_at).toFixed(2)}x</b></div>
                    <div>Reward Multiplier (service):</div><div className="text-right text-cyan-300"><b>{((selPlan.cashback_at * 1.5) / selPlan.total_at).toFixed(2)}x</b></div>
                  </div>
                </div>
                <div className="text-xs uppercase tracking-widest text-amber-300 font-black">1️⃣ Nominee Details (Mandatory)</div>
                <input value={nomName} onChange={(e) => setNomName(e.target.value)} placeholder="Nominee Full Name *" data-testid="corpus50-nom-name" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <input value={nomRel} onChange={(e) => setNomRel(e.target.value)} placeholder="Relation (Spouse/Son/etc) *" data-testid="corpus50-nom-rel" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <input value={nomMobile} onChange={(e) => setNomMobile(e.target.value)} placeholder="Nominee Mobile *" data-testid="corpus50-nom-mobile" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <div className="text-[10px] opacity-70">Optional (recommended for security):</div>
                <input value={nomAadhaar} onChange={(e) => setNomAadhaar(e.target.value)} placeholder="Nominee Aadhaar No. (12 digit)" data-testid="corpus50-nom-aadhaar" maxLength={14} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <input value={nomPan} onChange={(e) => setNomPan(e.target.value)} placeholder="Nominee PAN No." data-testid="corpus50-nom-pan" maxLength={10} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm uppercase" />

                <div className="text-xs uppercase tracking-widest text-amber-300 font-black mt-3">2️⃣ Your KYC (Optional — stored encrypted in browser)</div>
                <input value={kycAadhaarNo} onChange={(e) => setKycAadhaarNo(e.target.value)} placeholder="Your Aadhaar No." data-testid="corpus50-kyc-aadhaar-no" maxLength={14} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <label className="block text-xs">
                  <span className="opacity-80">Aadhaar Photo (≤2 MB)</span>
                  <input type="file" accept="image/*" onChange={(e) => onKycImg('aadhaar', e)} data-testid="corpus50-kyc-aadhaar-img" className="text-xs w-full mt-1" />
                </label>
                {kycAadhaarImg && <img src={kycAadhaarImg} alt="Aadhaar" className="w-full max-h-32 object-contain rounded-lg border border-slate-700" />}
                <input value={kycPanNo} onChange={(e) => setKycPanNo(e.target.value)} placeholder="Your PAN No." data-testid="corpus50-kyc-pan-no" maxLength={10} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm uppercase" />
                <label className="block text-xs">
                  <span className="opacity-80">PAN Photo (≤2 MB)</span>
                  <input type="file" accept="image/*" onChange={(e) => onKycImg('pan', e)} data-testid="corpus50-kyc-pan-img" className="text-xs w-full mt-1" />
                </label>
                {kycPanImg && <img src={kycPanImg} alt="PAN" className="w-full max-h-32 object-contain rounded-lg border border-slate-700" />}
                <div className="text-[10px] text-amber-200 opacity-80">🔒 KYC images stored ENCRYPTED in your browser only (localStorage). Not uploaded to server.</div>

                <button onClick={start} disabled={starting} data-testid="corpus50-start-btn" className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-300 text-black font-black">
                  {starting ? 'Starting…' : `Start ${selPlan.label} (${selPlan.monthly_at} AT)`}
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'my-plan' && active && (
          <div className="space-y-4">
            <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 to-orange-900/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[9px] uppercase opacity-70">Plan</div><div className="text-lg font-black">{active.plan_label}</div></div>
                <div><div className="text-[9px] uppercase opacity-70">Monthly S.I.P.</div><div className="text-lg font-black">{active.monthly_at.toLocaleString()} AT</div></div>
                <div><div className="text-[9px] uppercase opacity-70">Progress</div><div className="text-lg font-black">{active.months_paid} / 72</div></div>
                <div><div className="text-[9px] uppercase opacity-70">Next S.I.P. Due</div><div className="text-sm font-black">{new Date(active.next_due_at).toLocaleDateString()}{daysUntilDue !== null && <> · <span className={daysUntilDue <= 3 ? 'text-rose-300' : 'text-emerald-300'}>{daysUntilDue}d</span></>}</div></div>
                <div className="col-span-2"><div className="text-[9px] uppercase opacity-70">Receipt No</div><div className="text-xs font-mono">{active.receipt_no}</div></div>
              </div>
              {/* SIP progress bar */}
              <div className="mt-3 h-2 w-full bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-yellow-300" style={{ width: `${Math.min(100, (active.months_paid / 72) * 100)}%` }} />
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <button onClick={payEmi} disabled={paying} data-testid="corpus50-pay-emi" className="px-4 py-2 rounded-lg bg-emerald-500 text-black font-black text-sm">{paying ? 'Paying…' : 'Pay Next S.I.P.'}</button>
                <button onClick={sendReminder} data-testid="corpus50-send-reminder" className="px-4 py-2 rounded-lg bg-green-600 text-white font-black text-sm flex items-center gap-1"><Send className="w-3 h-3" /> WhatsApp Reminder</button>
                <button onClick={shareReferral} data-testid="corpus50-share-referral" className="px-4 py-2 rounded-lg bg-cyan-500 text-black font-black text-sm flex items-center gap-1"><Sparkles className="w-3 h-3" /> Share Plan</button>
                <button onClick={() => openBond(active.id)} disabled={bondLoading} data-testid="corpus50-view-bond" className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-300 to-yellow-400 text-black font-black text-sm flex items-center gap-1"><Shield className="w-3 h-3" /> {bondLoading ? 'Loading…' : 'View Bond & Consent'}</button>
              </div>
              {active.bond_no && <div className="mt-2 text-[10px] opacity-70">Bond No: <b className="text-amber-200 font-mono">{active.bond_no}</b></div>}
            </div>
            {/* Nominee */}
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4" data-testid="corpus50-nominee">
              <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black mb-2 flex items-center gap-1"><Shield className="w-3 h-3" /> NOMINEE</div>
              <div className="text-sm"><b>{active.nominee?.name}</b> · {active.nominee?.relation} · {active.nominee?.mobile}</div>
              {active.nominee?.aadhaar && <div className="text-[11px] opacity-80">Aadhaar: {active.nominee.aadhaar}</div>}
              {active.nominee?.pan && <div className="text-[11px] opacity-80">PAN: {active.nominee.pan}</div>}
            </div>
            {/* Badges */}
            {(config?.user?.badges || []).length > 0 && (
              <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
                <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black mb-2"><Award className="inline w-3 h-3" /> BADGES</div>
                <div className="flex gap-2 flex-wrap">
                  {config.user.badges.map((b) => (
                    <div key={b.id} className="px-2 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-xs font-black">{b.emoji} {b.label}</div>
                  ))}
                </div>
              </div>
            )}
            {/* Foreclose */}
            <div className="rounded-2xl border-2 border-rose-400/40 bg-rose-500/10 p-4" data-testid="corpus50-foreclose-panel">
              <div className="text-[9px] uppercase tracking-widest text-rose-300 font-black mb-1">⚠️ FORECLOSE (Cancel Plan)</div>
              <div className="text-xs opacity-80 mb-2">Only within 15 days of starting. 70% refund of paid amount (30% deduction). After 15 days: foreclosure not allowed — must continue or auto-close after 180 days overdue.</div>
              <button onClick={foreclose} data-testid="corpus50-foreclose-btn" className="px-4 py-2 rounded-lg bg-rose-500 text-white font-black text-sm">Request Foreclosure</button>
            </div>
          </div>
        )}

        {tab === 'history' && (
          <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
            <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-3">Payment History ({payments.length})</div>
            {payments.length === 0 ? <div className="text-sm text-slate-400 text-center py-6">Abhi tak koi payment nahi.</div> : (
              <div className="space-y-2">
                {payments.map((p) => (
                  <div key={p.id} className="p-3 rounded-lg bg-slate-800 flex justify-between items-center flex-wrap gap-1">
                    <div>
                      <div className="text-sm">S.I.P. {p.month_index}/72 · <b>{p.amount.toLocaleString()} AT</b> {p.penalty_pct > 0 && <span className="text-rose-400 text-[10px]"> (penalty {p.penalty_pct}%)</span>}</div>
                      <div className="text-[10px] opacity-60 font-mono">{p.receipt_no}</div>
                    </div>
                    <div className="text-[10px] opacity-70">{new Date(p.paid_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'board' && leaderboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-2">🏆 Top Referrers</div>
              {(leaderboard.top_referrers || []).map((r, i) => (
                <div key={r._id} className="flex justify-between py-1 text-sm"><span>#{i + 1} {r.username}</span><b>{r.earned.toFixed(2)} AT</b></div>
              ))}
            </div>
            <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-3">
              <div className="text-xs uppercase tracking-widest text-emerald-300 font-black mb-2">💰 Top Donators</div>
              {(leaderboard.top_donators || []).map((r, i) => (
                <div key={r._id} className="flex justify-between py-1 text-sm"><span>#{i + 1} {r.username}</span><b>{r.total.toLocaleString()} AT</b></div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 rounded-lg border border-slate-800 bg-black/50 p-3 text-[10px] text-slate-400 text-center" data-testid="corpus50-disclaimer">
          ⚠️ <b>Terms, Conditions &amp; Legal Disclaimer:</b> Valid from 1 Aug 2026 to 31 Dec 2028. Voluntary crowdfunding <b>S.I.P. (Systematic Impact Pledge)</b> — this is <b>NOT a financial investment</b>. In case of dispute, platform/founders have no legal or financial liability.
        </div>
      </div>

      {/* === Consent Modal (first SIP mandatory) === */}
      {showConsent && selPlan && (
        <div className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center p-3" data-testid="corpus50-consent-modal">
          <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-amber-400/50 bg-gradient-to-b from-slate-900 to-slate-950 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-lg font-black text-amber-300 flex items-center gap-1"><Shield className="w-5 h-5" /> Corpus50 · Crowdfund Consent &amp; Disclaimer</div>
              <button onClick={() => setShowConsent(false)} className="text-xs opacity-60">✕</button>
            </div>
            <div className="text-xs opacity-90 space-y-2 border border-amber-400/20 bg-amber-500/5 rounded-lg p-3 max-h-[45vh] overflow-y-auto" data-testid="corpus50-consent-text">
              <div className="text-[11px] font-black text-amber-200">Please read carefully. Your agreement below is stored as PROOF of consent.</div>
              <div><b>1. Nature of Programme:</b> Anant Corpus50 is a voluntary <b>community crowdfund contribution</b> to build the Samadhan Center — <b>NOT a financial investment, savings, insurance, or securities product</b>. There is no guaranteed monetary return, no listing, and no regulatory registration as a financial instrument.</div>
              <div><b>2. Selected Plan:</b> {selPlan.label} · <b>{selPlan.monthly_at.toLocaleString()} AT / month</b> × 72 months = <b>{selPlan.total_at.toLocaleString()} AT</b> total contribution.</div>
              <div><b>3. Redemption Rewards:</b> On completion, you may choose EITHER (a) Cash-out/Transfer redemption of <b>{selPlan.cashback_at.toLocaleString()} AT</b> OR (b) Samadhan Centre service redemption of <b>{Math.round(selPlan.cashback_at * 1.5).toLocaleString()} AT</b> (1.5× bonus). These rewards are community-funded and honoured on best-effort basis by the programme.</div>
              <div><b>4. Nominee &amp; Safety:</b> A verified nominee is mandatory. In the unfortunate event of the donor&apos;s demise, nominee automatically inherits the accumulated redemption entitlement per programme rules.</div>
              <div><b>5. Late Payment:</b> Grace period of 7 days. 5% penalty (8-90 days late), 10% penalty (91-180 days). 180+ days overdue = auto-close of plan.</div>
              <div><b>6. Foreclosure:</b> Allowed only within first 15 days of starting. 70% refund of contributions paid (30% administrative deduction). After 15 days, foreclosure is not allowed.</div>
              <div><b>7. Nominee KYC:</b> Any Aadhaar/PAN submitted is stored encrypted in your own browser (client-side). Anant Web Portal does not centrally warehouse this information.</div>
              <div><b>8. No Legal / Financial Liability:</b> By joining, you agree that the Anant Web Portal team, founders, referrers, and admins have <b>no legal, financial, or fiduciary liability</b> in case of programme changes, delays, force majeure, or dispute.</div>
              <div><b>9. Jurisdiction:</b> Any dispute shall be resolved via bilateral discussion. Applicable Indian jurisdiction, without warranty of enforceability against volunteer community programme.</div>
              <div><b>10. Voluntary Confirmation:</b> I confirm this contribution is voluntary, from my legitimate funds, and I am not under any coercion.</div>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded-lg bg-amber-500/10 border border-amber-400/30">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="corpus50-consent-checkbox" className="mt-1" />
              <span>I have read and <b>fully agree</b> to the above disclaimer &amp; terms. I understand this is a <b>crowdfund contribution</b>, not a financial investment. My agreement will be recorded with timestamp as proof.</span>
            </label>
            <div className="flex gap-2">
              <button onClick={() => setShowConsent(false)} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-white text-sm">Cancel</button>
              <button onClick={confirmAndStart} disabled={!agreed || starting} data-testid="corpus50-consent-agree" className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-yellow-300 text-black font-black text-sm disabled:opacity-40">{starting ? 'Starting…' : `✅ I Agree · Start ${selPlan.monthly_at} AT SIP`}</button>
            </div>
          </div>
        </div>
      )}

      {/* === Bond & Consent Viewer — Premium Themed Certificate === */}
      {bondModal && (() => {
        const theme = CERT_THEMES[certTheme] || CERT_THEMES.royal_blue_gold;
        return (
        <div className="fixed inset-0 bg-black/90 z-[110] flex items-center justify-center p-3 print:bg-white print:p-0" data-testid="corpus50-bond-modal">
          <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-2xl bg-transparent p-3 relative print:max-h-none print:overflow-visible print:p-0">
            <button onClick={() => setBondModal(null)} className="absolute top-2 right-4 text-white text-2xl opacity-80 z-10 print:hidden">✕</button>

            {/* Theme selector */}
            <div className="mb-3 rounded-xl border border-white/20 bg-black/60 p-2 print:hidden" data-testid="corpus50-theme-picker">
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-black mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Certificate Theme</div>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {Object.entries(CERT_THEMES).map(([k, t]) => (
                  <button
                    key={k}
                    onClick={() => { setCertTheme(k); localStorage.setItem('corpus50_cert_theme_user_picked', '1'); }}
                    data-testid={`cert-theme-${k}`}
                    className={`flex-shrink-0 rounded-lg border-2 p-1.5 transition-all ${certTheme === k ? 'border-white scale-105 shadow-lg' : 'border-white/20 hover:border-white/60'}`}
                    title={t.label}
                  >
                    <div className="w-16 h-8 rounded" style={{ background: t.ribbon }}></div>
                    <div className="text-[9px] font-black text-white mt-0.5">{t.emoji} {t.label.split(' ')[0]}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Premium Certificate — themed */}
            <div id="corpus50-certificate" className="relative p-2 rounded-lg" style={{ background: theme.parchment, boxShadow: '0 25px 50px rgba(0,0,0,0.5)' }}>
              {/* Guilloché SVG background layer */}
              <div className="absolute inset-2 rounded-md pointer-events-none opacity-70" style={{ backgroundImage: guillocheSVG(theme.guillocheColor1, theme.guillocheColor2), backgroundSize: '240px 240px' }}></div>
              {/* Outer border */}
              <div className="relative border-[3px] rounded-md p-2" style={{ borderColor: theme.border1 }}>
                {/* Inner ornate border */}
                <div className="relative border rounded p-6" style={{ borderColor: theme.border2, backgroundImage: `radial-gradient(circle at 0% 0%, ${theme.guillocheColor2} 0%, transparent 60%), radial-gradient(circle at 100% 100%, ${theme.guillocheColor1} 0%, transparent 60%)` }}>
                  {/* Corner flourishes */}
                  <div className="absolute top-2 left-2 w-10 h-10 border-t-4 border-l-4 rounded-tl-lg" style={{ borderColor: theme.gold }}></div>
                  <div className="absolute top-2 right-2 w-10 h-10 border-t-4 border-r-4 rounded-tr-lg" style={{ borderColor: theme.gold }}></div>
                  <div className="absolute bottom-2 left-2 w-10 h-10 border-b-4 border-l-4 rounded-bl-lg" style={{ borderColor: theme.gold }}></div>
                  <div className="absolute bottom-2 right-2 w-10 h-10 border-b-4 border-r-4 rounded-br-lg" style={{ borderColor: theme.gold }}></div>

                  {/* HEADER — Themed ribbon banner */}
                  <div className="relative flex justify-center mb-3">
                    <div className="relative px-8 py-2 shadow-lg" style={{ background: theme.ribbon, clipPath: 'polygon(4% 0, 96% 0, 100% 50%, 96% 100%, 4% 100%, 0 50%)' }}>
                      <div className="text-2xl sm:text-3xl font-black tracking-widest" style={{ color: certTheme === 'midnight_platinum' ? '#f8fafc' : (certTheme === 'emerald_regal' || certTheme === 'crimson_gold' || certTheme === 'saffron_maroon' ? '#fef3c7' : theme.ink), textShadow: '1px 1px 0 rgba(255,255,255,0.35), 0 0 20px rgba(255,215,120,0.4)', fontFamily: '"Cinzel", "Playfair Display", Georgia, serif' }}>ANANT CORPUS50 FUND</div>
                    </div>
                  </div>
                  <div className="text-center mb-1">
                    <div className="text-xs sm:text-sm font-black tracking-[0.3em]" style={{ color: theme.accent, fontFamily: '"Cinzel", Georgia, serif' }}>SIP BOND · COMMUNITY DIGITAL AGREEMENT</div>
                  </div>
                  <div className="text-center mb-4">
                    <div className="text-3xl sm:text-4xl" style={{ color: theme.ink, fontFamily: '"Playfair Display", "Cormorant Garamond", "Times New Roman", serif', fontStyle: 'italic', fontWeight: 700, textShadow: `0 1px 0 ${theme.goldLight}` }}>Certificate of Community Pledge</div>
                    <div className="w-64 h-1 mx-auto mt-2" style={{ background: `linear-gradient(90deg, transparent, ${theme.gold}, transparent)` }}></div>
                  </div>

                  {/* MAIN CONTENT — 2 col grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-4 relative">
                    {/* LEFT COL */}
                    <div className="sm:col-span-2 space-y-3" style={{ color: theme.ink }}>
                      <div className="rounded border p-2" style={{ borderColor: `${theme.accent}55`, background: 'rgba(255,255,255,0.45)' }}>
                        <div className="text-[9px] uppercase tracking-widest font-black mb-0.5" style={{ color: theme.accent }}>👤 Donor Information</div>
                        <div className="text-sm font-black" style={{ fontFamily: 'Georgia,serif' }}>{bondModal.bond.user?.name || '—'}</div>
                        <div className="text-[10px] opacity-80">@{bondModal.bond.user?.username || 'anonymous'}</div>
                        <div className="text-[10px] opacity-70">Started: {bondModal.bond.started_at ? new Date(bondModal.bond.started_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</div>
                      </div>
                      <div className="rounded border p-2" style={{ borderColor: `${theme.accent}55`, background: 'rgba(255,255,255,0.45)' }}>
                        <div className="text-[9px] uppercase tracking-widest font-black mb-0.5" style={{ color: theme.accent }}>🎗️ Nominee Details</div>
                        <div className="text-sm font-black" style={{ fontFamily: 'Georgia,serif' }}>{bondModal.bond.nominee?.name || '—'} {bondModal.bond.nominee?.relation && <span className="text-[10px] font-normal opacity-70">({bondModal.bond.nominee.relation})</span>}</div>
                        <div className="text-[10px] opacity-80 font-mono">{bondModal.bond.nominee?.mobile || '—'}</div>
                        {bondModal.bond.nominee?.aadhaar && <div className="text-[10px] opacity-70">Aadhaar ****{String(bondModal.bond.nominee.aadhaar).slice(-4)}</div>}
                        {bondModal.bond.nominee?.pan && <div className="text-[10px] opacity-70">PAN {bondModal.bond.nominee.pan}</div>}
                      </div>
                      <div className="rounded border p-2" style={{ borderColor: `${theme.accent}55`, background: 'rgba(255,255,255,0.45)' }}>
                        <div className="text-[9px] uppercase tracking-widest font-black mb-0.5" style={{ color: theme.accent }}>📜 Bond Particulars</div>
                        <div className="text-[11px]">Bond No: <b className="font-mono">{bondModal.bond.bond_no}</b></div>
                        <div className="text-[11px]">Receipt: <b className="font-mono">{bondModal.bond.receipt_no}</b></div>
                      </div>
                      <div className="rounded border-2 p-2" style={{ borderColor: theme.gold, background: `linear-gradient(135deg, ${theme.goldLight}44, ${theme.goldLight}22)` }}>
                        <div className="text-[9px] uppercase tracking-widest font-black mb-1" style={{ color: theme.accent }}>💰 Plan Specifications</div>
                        <div className="text-lg font-black" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>{bondModal.bond.plan?.label || 'Corpus Starter'}</div>
                        <div className="text-xs font-bold">{bondModal.bond.plan?.monthly_at?.toLocaleString() || '0'} AT/mo</div>
                        <div className="text-[10px] opacity-80">Total: {bondModal.bond.plan?.total_at?.toLocaleString() || '0'} AT (72 months)</div>
                        <div className="mt-2">
                          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: `${theme.accent}22` }}>
                            <div className="h-full" style={{ width: `${((bondModal.bond.months_paid || 0) / 72) * 100}%`, background: `linear-gradient(90deg, ${theme.accent}, ${theme.gold})` }}></div>
                          </div>
                          <div className="text-[10px] mt-0.5 flex justify-between"><span>{bondModal.bond.months_paid || 0}/72 paid</span><span>Next SIP: {bondModal.bond.next_due_at ? new Date(bondModal.bond.next_due_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</span></div>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT COL: Clause Summary + Seal */}
                    <div className="sm:col-span-3 relative">
                      <div className="text-center mb-2">
                        <div className="inline-block px-3 py-0.5 text-[10px] font-black tracking-widest uppercase" style={{ background: theme.accent, color: theme.goldLight, fontFamily: '"Cinzel", Georgia, serif' }}>Clause Summary</div>
                      </div>
                      <div className="space-y-1.5 text-[10px] sm:text-[11px] leading-snug relative z-10" style={{ color: theme.ink }}>
                        {(bondModal.bond.clause_summary || []).slice(0, 10).map((c, i) => (
                          <div key={i} className="flex gap-2">
                            <div className="flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center" style={{ background: theme.accent, color: theme.goldLight }}>{i + 1}</div>
                            <div className="flex-1">{c}</div>
                          </div>
                        ))}
                      </div>
                      {/* Holographic seal */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-25">
                        <div className="w-36 h-36 rounded-full p-1" style={{ background: theme.sealBg, boxShadow: `0 0 30px ${theme.gold}88` }}>
                          <div className="w-full h-full rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.85)', backgroundImage: `repeating-radial-gradient(circle, ${theme.gold}33 0 2px, transparent 2px 6px)` }}>
                            <div className="text-4xl font-black bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.gold} 50%, ${theme.accent} 100%)`, fontFamily: '"Cinzel", Georgia, serif' }}>C50</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* FOOTER */}
                  <div className="border-t-2 pt-3 mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end" style={{ borderColor: `${theme.accent}44` }}>
                    <div>
                      <div className="text-2xl font-black" style={{ color: theme.accent, fontFamily: '"Playfair Display", Georgia, serif', textShadow: `0 1px 0 ${theme.goldLight}` }}>Anant</div>
                      <div className="text-[9px] opacity-80" style={{ color: theme.ink }}>Web Portal · Corpus50 Trust Council</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl italic" style={{ color: theme.accent, fontFamily: '"Great Vibes", cursive', textShadow: `0 1px 0 ${theme.goldLight}` }}>Anant Trust</div>
                      <div className="border-t mt-1 pt-1 text-[9px] font-black uppercase tracking-widest" style={{ borderColor: `${theme.ink}66`, color: theme.ink }}>Digitally Signed · C50 Verified</div>
                    </div>
                    <div className="flex items-end justify-end gap-3">
                      <div className="text-right">
                        <div className="text-[9px] font-black uppercase tracking-wider" style={{ color: theme.accent }}>Authorised Signatory</div>
                        <div className="text-[10px] opacity-90" style={{ color: theme.ink }}>{bondModal.bond.authority?.signatory || 'Corpus50 Trust Council'}</div>
                        <div className="text-[9px] opacity-70" style={{ color: theme.ink }}>Issued: {bondModal.bond.authority?.issued_on ? new Date(bondModal.bond.authority.issued_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</div>
                      </div>
                      <div className="flex-shrink-0 w-16 h-16 p-1 border" style={{ background: '#fff', borderColor: `${theme.ink}55` }}>
                        <img alt="QR" src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`https://anantwebportal.com/verify/corpus50/${bondModal.bond.bond_no}`)}`} className="w-full h-full" />
                      </div>
                    </div>
                  </div>

                  {/* Timestamp watermark */}
                  <div className="absolute bottom-1 right-4 text-[8px] font-mono" style={{ color: `${theme.ink}80` }}>{bondModal.bond.authority?.issued_on ? new Date(bondModal.bond.authority.issued_on).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
                </div>
              </div>
            </div>

            {/* Consent record + actions */}
            {bondModal.consent && (
              <div className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-3 text-[11px] text-white print:hidden" data-testid="corpus50-consent-record">
                <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-black mb-1">✅ Consent Record</div>
                <div>Agreed by <b>@{bondModal.consent.username}</b> at <b>{new Date(bondModal.consent.agreed_at).toLocaleString()}</b>.</div>
                <div className="opacity-70 text-[10px] mt-0.5">Consent template: {bondModal.consent.consent_text_hash || 'corpus50-consent-v1'}</div>
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2 print:hidden">
              <button onClick={() => window.print()} className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-yellow-300 text-black text-sm font-black" data-testid="corpus50-bond-print">🖨️ Print / Save as PDF</button>
              <button
                onClick={async () => {
                  try {
                    const html2canvas = (await import('html2canvas')).default;
                    const node = document.getElementById('corpus50-certificate');
                    const canvas = await html2canvas(node, { scale: 2, backgroundColor: null, useCORS: true, allowTaint: true });
                    const link = document.createElement('a');
                    link.download = `Corpus50-${bondModal.bond.bond_no}-${certTheme}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                    toast.success('Certificate saved as PNG');
                  } catch (e) {
                    toast.error('Download failed — use Print instead');
                  }
                }}
                data-testid="corpus50-bond-png"
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500 text-black text-sm font-black">📥 Download PNG</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
